import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { cn } from '@lombok/ui';
import { useAuth } from '@/auth/AuthContext';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { routeGroupForRole, ROLE_LABELS_ES } from '@/auth/roles';
import { GROUP_TITLES_ES, NAV_BY_GROUP } from '@/app/navigation';
import { IconClose, IconMenu, IconMoon, IconSun } from '@/components/icons';
import { Logo } from '@/components/Logo';
import { UserMenu } from '@/components/UserMenu';
import { useCanCreateOrders } from '@/auth/useCanCreateOrders';
import { DistributorStatusBanner } from '@/features/distributors/DistributorStatusBanner';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import {
  readSidebar,
  readTheme,
  saveSidebar,
  saveTheme,
  type SidebarMode,
  type Theme,
} from '@/lib/shellPrefs';

/**
 * Authenticated app shell. Mobile-first: the sidebar is an off-canvas drawer on
 * small screens (large tap targets, full-height) and a fixed rail on desktop that
 * can shrink to an icon-only strip. Navigation is role-aware — only the current
 * role's Spanish nav is rendered. The color theme (dark by default) and the
 * sidebar state are remembered per browser.
 */
export function AppShell() {
  const { session, signOut } = useAuth();
  // `/me` is the authoritative profile (name/role); the session is the instant
  // bootstrap. Prefer the resolved profile for display, fall back to the session.
  const { profile } = useSessionProfile();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const [sidebar, setSidebar] = useState<SidebarMode>(() => readSidebar());
  const canCreateOrders = useCanCreateOrders();

  // The theme belongs to the signed-in app only: it is set on <html> while the shell is mounted
  // (index.html does the same before the first paint) and removed on sign-out, so the public
  // landing and the login keep their own look.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    return () => document.documentElement.removeAttribute('data-theme');
  }, [theme]);

  if (!session) return null; // RoleGuard handles redirects; this satisfies types.

  // Route group uses the session role (available instantly on load).
  const group = routeGroupForRole(session.user.role);
  // A distributor whose account cannot order does not get the "Crear pedido" entry at all.
  const items =
    group === 'distributor' && !canCreateOrders
      ? NAV_BY_GROUP[group].filter((item) => item.to !== '/app/distributor/crear-pedido')
      : NAV_BY_GROUP[group];
  const display = profile ?? session.user;
  const collapsed = sidebar === 'collapsed';

  const toggleTheme = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    saveTheme(next);
  };
  const toggleSidebar = () => {
    const next: SidebarMode = collapsed ? 'expanded' : 'collapsed';
    setSidebar(next);
    saveSidebar(next);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-dvh bg-surface-muted">
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-surface px-4">
        <button
          type="button"
          className="-ml-1 inline-flex h-10 w-10 items-center justify-center rounded-md text-text-primary hover:bg-surface-muted lg:hidden"
          aria-label="Abrir menú"
          onClick={() => setMobileOpen(true)}
        >
          <IconMenu />
        </button>
        <button
          type="button"
          className="-ml-1 hidden h-10 w-10 items-center justify-center rounded-md text-text-primary hover:bg-surface-muted lg:inline-flex"
          aria-label={collapsed ? 'Mostrar menú lateral' : 'Ocultar menú lateral'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Mostrar menú lateral' : 'Ocultar menú lateral'}
          onClick={toggleSidebar}
        >
          <IconMenu />
        </button>
        <Logo className="h-7" />
        <span className="hidden text-sm font-medium text-text-muted sm:inline">
          {GROUP_TITLES_ES[group]}
        </span>
        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-text-primary hover:bg-surface-muted"
          >
            {theme === 'dark' ? <IconSun /> : <IconMoon />}
          </button>
          <NotificationBell notificationsPath={`/app/${group}/notificaciones`} />
          <UserMenu
            name={display.fullName}
            roleLabel={ROLE_LABELS_ES[display.role]}
            onSignOut={handleSignOut}
          />
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar: full width, or an icon-only rail. The scrollbar only shows on hover. */}
        <aside
          className={cn(
            'scrollbar-hover sticky top-14 hidden h-[calc(100dvh-3.5rem)] shrink-0 overflow-y-auto overflow-x-hidden border-r border-border bg-surface py-4 transition-[width] duration-200 motion-reduce:transition-none lg:block',
            collapsed ? 'w-16 px-2' : 'w-64 px-3',
          )}
        >
          <SidebarNav items={items} collapsed={collapsed} />
        </aside>

        {/* Mobile drawer — backdrop fades, panel slides in on an iOS-style curve.
            MotionConfig reducedMotion="user" drops the transforms for users who
            ask for reduced motion (the panel then just crossfades). */}
        <MotionConfig reducedMotion="user">
          <AnimatePresence>
            {mobileOpen ? (
              <div className="fixed inset-0 z-40 lg:hidden">
                <motion.button
                  type="button"
                  aria-label="Cerrar menú"
                  className="absolute inset-0 bg-brand-bg/50 backdrop-blur-sm"
                  onClick={() => setMobileOpen(false)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                />
                <motion.nav
                  className="scrollbar-hover absolute inset-y-0 left-0 flex w-72 max-w-[85%] flex-col overflow-y-auto bg-surface px-3 py-4 shadow-lg"
                  initial={{ x: '-100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '-100%' }}
                  transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
                >
                  <div className="mb-2 flex items-center justify-between px-1">
                    <Logo className="h-7" />
                    <button
                      type="button"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-md text-text-primary transition-colors hover:bg-surface-muted"
                      aria-label="Cerrar menú"
                      onClick={() => setMobileOpen(false)}
                    >
                      <IconClose />
                    </button>
                  </div>
                  <SidebarNav items={items} onNavigate={() => setMobileOpen(false)} />
                </motion.nav>
              </div>
            ) : null}
          </AnimatePresence>
        </MotionConfig>

        {/* Content */}
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          {/* The sidebar hugs the left edge; the page is centered in the space that is left,
              capped so lines do not stretch on very wide screens. Child routes render here. */}
          <div className="mx-auto w-full max-w-[1144px]">
            {group === 'distributor' ? <DistributorStatusBanner /> : null}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarNav({
  items,
  onNavigate,
  collapsed = false,
}: {
  items: typeof NAV_BY_GROUP[keyof typeof NAV_BY_GROUP];
  onNavigate?: () => void;
  /** Icon-only rail (desktop). The label stays for screen readers and as a tooltip. */
  collapsed?: boolean;
}) {
  return (
    <ul className="flex flex-col gap-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.to.split('/').length <= 3}
            onClick={onNavigate}
            title={collapsed ? item.label : undefined}
            aria-label={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              cn(
                'relative flex min-h-11 items-center gap-3 rounded-md text-sm font-medium transition-colors duration-150',
                collapsed ? 'justify-center px-0' : 'px-3',
                isActive
                  ? 'bg-accent-soft text-brand-primary'
                  : 'text-text-muted hover:bg-surface-muted hover:text-text-primary',
              )
            }
          >
            {({ isActive }) => (
              <>
                {/* Gold "you are here" indicator — a short rounded pill, not a
                    full-height border stripe. */}
                {isActive ? (
                  <span
                    className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-brand-accent"
                    aria-hidden="true"
                  />
                ) : null}
                <item.icon className="shrink-0" />
                <span className={cn('truncate', collapsed && 'sr-only')}>{item.label}</span>
              </>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
